import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Stack,
  Card,
  CardContent,
  alpha,
  Divider,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import StepConnector, {
  stepConnectorClasses,
} from "@mui/material/StepConnector";
import { InfoOutlined } from "@mui/icons-material";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { MuiTelInput } from "mui-tel-input";
import { CompanyInfo } from "./SettingsLayout";
import { URLS } from "../../constants";
import { useToast } from "../../components/ToastProvider";
import businessIcon from "../../assets/settings/settings_business.svg";
import api from "../../utils/api";

const CustomConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: { top: 12 },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: alpha(theme.palette.primary.main, 0.3),
    borderTopWidth: 3,
    borderRadius: 1,
  },
}));

const CustomStepIconRoot = styled("div")<{
  ownerState: { active?: boolean; completed?: boolean };
}>(({ theme, ownerState }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.2),
  width: 32,
  height: 32,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "14px",
  fontWeight: 600,
  color: theme.palette.primary.main,
  border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
  ...(ownerState.active && {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    border: `2px solid ${theme.palette.primary.main}`,
  }),
  ...(ownerState.completed && {
    backgroundColor: theme.palette.secondary.main,
    color: "white",
    border: `2px solid ${theme.palette.secondary.main}`,
  }),
}));

function CustomStepIcon(props: any) {
  const { active, completed, icon } = props;
  return (
    <CustomStepIconRoot ownerState={{ active, completed }}>
      {icon}
    </CustomStepIconRoot>
  );
}

export const BusinessSchema = (t: (key: string) => string) =>
  yup
    .object({
      name: yup.string().required(t("businessPage.error.name")),
      fantasy_name: yup.string().required(t("businessPage.error.fantasy_name")),
      cnpj: yup.string().required(t("businessPage.error.cnpj")),
      area: yup.string().required(t("businessPage.error.area")),
      address: yup
        .object({
          zip_code: yup.string().required(t("businessPage.error.zip_code")),
          street: yup.string().required(t("businessPage.error.street")),
          number: yup.string().required(t("businessPage.error.number")),
          city: yup.string().required(t("businessPage.error.city")),
          state: yup.string().required(t("businessPage.error.state")),
          country: yup.string().required(t("businessPage.error.country")),
        })
        .required(),
      email: yup
        .string()
        .email(t("businessPage.error.email"))
        .required(t("businessPage.error.email")),
      website: yup
        .string()
        .url(t("businessPage.error.website"))
        .required(t("businessPage.error.website")),
      instagram_profile: yup
        .string()
        .required(t("businessPage.error.instagram_profile")),
      telephone: yup.string().required(t("businessPage.error.telephone")), // now a single string
    })
    .required();

type FormValues = yup.InferType<ReturnType<typeof BusinessSchema>>;
const steps = [
  "businessPage.step.company",
  "businessPage.step.address",
  "businessPage.step.contact",
];

interface BusinessPageProps {
  companyInfo: CompanyInfo;
}

export default function BusinessPage({ companyInfo }: BusinessPageProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { showToast } = useToast();

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: yupResolver(BusinessSchema(t)),
    defaultValues: {} as any,
    mode: "onBlur",
    shouldUnregister: false,
  });

  const [activeStep, setActiveStep] = useState(0);
  const [company, setCompany] = useState<CompanyInfo>(companyInfo);
  const handleNext = () =>
    setActiveStep(i => Math.min(i + 1, steps.length - 1));
  const handleBack = () => setActiveStep(i => Math.max(i - 1, 0));

  useEffect(() => {
    setCompany(companyInfo);
  }, [companyInfo]);

  useEffect(() => {
    if (activeStep === 0) {
      setValue("name", companyInfo.name);
      setValue("fantasy_name", companyInfo.fantasy_name);
      setValue("cnpj", companyInfo.cnpj);
      setValue("area", companyInfo.area);
    } else if (activeStep === 1) {
      setValue("address.zip_code", companyInfo.address?.zip_code || "");
      setValue("address.street", companyInfo.address?.street || "");
      setValue("address.number", companyInfo.address?.number || "");
      setValue("address.city", companyInfo.address?.city || "");
      setValue("address.state", companyInfo.address?.state || "");
      setValue("address.country", companyInfo.address?.country || "");
    } else if (activeStep === 2) {
      setValue("email", companyInfo.email);
      setValue("website", companyInfo.website);
      setValue("instagram_profile", companyInfo.instagram_profile);
      setValue("telephone", companyInfo.telephone?.[0] || "");
      setTimeout(() => {
      }, 100);
    }
  }, [activeStep, companyInfo, setValue, getValues]);

  const onSubmit: SubmitHandler<FormValues> = async data => {
    const payload: Partial<CompanyInfo> = {
      fantasy_name: data.fantasy_name,
      area: data.area,
      website: data.website,
      instagram_profile: data.instagram_profile,
    };

    try {
      const res = await api.put(`${URLS.CLIENT_URL}/api/company`, payload);
      if (res.status !== 200) throw new Error("Failed to update company");

      const updated = { ...company, ...payload };
      setCompany(updated);

      reset({
        name: updated.name,
        fantasy_name: updated.fantasy_name,
        cnpj: updated.cnpj,
        area: updated.area,
        email: updated.email,
        website: updated.website,
        instagram_profile: updated.instagram_profile,
        telephone: updated.telephone?.[0] || "",
        address: updated.address,
      });

      setActiveStep(0);

      showToast(t("toast.companyUpdated"), "success");
    } catch (err) {
      showToast(t("toast.errorCompanyUpdate"), "error");
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Stack spacing={3}>
            {/* Company and trade name */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                  }}
                >
                  {t("businessPage.field.name")}
                </Typography>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      disabled
                      placeholder={t("placeholder.companyName")}
                      fullWidth
                      variant="outlined"
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: alpha(theme.palette.grey[100], 0.5),
                        },
                      }}
                    />
                  )}
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                  }}
                >
                  {t("businessPage.field.fantasy_name")}
                </Typography>
                <Controller
                  name="fantasy_name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      placeholder={t("placeholder.fantasyName")}
                      fullWidth
                      variant="outlined"
                      error={!!errors.fantasy_name}
                      helperText={errors.fantasy_name?.message}
                    />
                  )}
                />
              </Box>
            </Box>

            {/* CNPJ and Area */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                  }}
                >
                  {t("businessPage.field.cnpj")}
                </Typography>
                <Controller
                  name="cnpj"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      disabled
                      placeholder={t("placeholder.companyCnpj")}
                      fullWidth
                      variant="outlined"
                      error={!!errors.cnpj}
                      helperText={errors.cnpj?.message}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: alpha(theme.palette.grey[100], 0.5),
                        },
                      }}
                    />
                  )}
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                  }}
                >
                  {t("businessPage.field.area")}
                </Typography>
                <Controller
                  name="area"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      placeholder={t("placeholder.businessArea")}
                      fullWidth
                      variant="outlined"
                      error={!!errors.area}
                      helperText={errors.area?.message}
                    />
                  )}
                />
              </Box>
            </Box>
          </Stack>
        );

      case 1:
        return (
          <Stack spacing={3}>
            {/* CEP e Rua */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                  }}
                >
                  {t("businessPage.field.zip_code")}
                </Typography>
                <Controller
                  name="address.zip_code"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      disabled
                      placeholder={t("placeholder.zipCode")}
                      fullWidth
                      variant="outlined"
                      error={!!errors.address?.zip_code}
                      helperText={errors.address?.zip_code?.message}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: alpha(theme.palette.grey[100], 0.5),
                        },
                      }}
                    />
                  )}
                />
              </Box>

              <Box sx={{ flex: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                  }}
                >
                  {t("businessPage.field.street")}
                </Typography>
                <Controller
                  name="address.street"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      disabled
                      placeholder={t("placeholder.address")}
                      fullWidth
                      variant="outlined"
                      error={!!errors.address?.street}
                      helperText={errors.address?.street?.message}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: alpha(theme.palette.grey[100], 0.5),
                        },
                      }}
                    />
                  )}
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                  }}
                >
                  {t("businessPage.field.number")}
                </Typography>
                <Controller
                  name="address.number"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      disabled
                      placeholder={t("placeholder.number")}
                      fullWidth
                      variant="outlined"
                      error={!!errors.address?.number}
                      helperText={errors.address?.number?.message}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: alpha(theme.palette.grey[100], 0.5),
                        },
                      }}
                    />
                  )}
                />
              </Box>
            </Box>

            {/* City, State and Country */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                  }}
                >
                  {t("businessPage.field.city")}
                </Typography>
                <Controller
                  name="address.city"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      disabled
                      placeholder={t("placeholder.city")}
                      fullWidth
                      variant="outlined"
                      error={!!errors.address?.city}
                      helperText={errors.address?.city?.message}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: alpha(theme.palette.grey[100], 0.5),
                        },
                      }}
                    />
                  )}
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                  }}
                >
                  {t("businessPage.field.state")}
                </Typography>
                <Controller
                  name="address.state"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      disabled
                      placeholder={t("placeholder.state")}
                      fullWidth
                      variant="outlined"
                      error={!!errors.address?.state}
                      helperText={errors.address?.state?.message}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: alpha(theme.palette.grey[100], 0.5),
                        },
                      }}
                    />
                  )}
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                  }}
                >
                  {t("businessPage.field.country")}
                </Typography>
                <Controller
                  name="address.country"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      disabled
                      placeholder={t("placeholder.country")}
                      fullWidth
                      variant="outlined"
                      error={!!errors.address?.country}
                      helperText={errors.address?.country?.message}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: alpha(theme.palette.grey[100], 0.5),
                        },
                      }}
                    />
                  )}
                />
              </Box>
            </Box>
          </Stack>
        );

      case 2:
        return (
          <Stack spacing={3}>
            {/* Email and Website */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                  }}
                >
                  {t("businessPage.field.email")}
                </Typography>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      disabled
                      placeholder={t("placeholder.companyEmail")}
                      type="email"
                      fullWidth
                      variant="outlined"
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: alpha(theme.palette.grey[100], 0.5),
                        },
                      }}
                    />
                  )}
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                  }}
                >
                  {t("businessPage.field.website")}
                </Typography>
                <Controller
                  name="website"
                  control={control}
                  render={({ field }) => {
                    return (
                      <TextField
                        {...field}
                        placeholder={t("placeholder.companyWebsite")}
                        fullWidth
                        variant="outlined"
                        error={!!errors.website}
                        helperText={errors.website?.message}
                      />
                    );
                  }}
                />
              </Box>
            </Box>

            {/* Instagram and Phone */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                  }}
                >
                  {t("businessPage.field.instagram_profile")}
                </Typography>
                <Controller
                  name="instagram_profile"
                  control={control}
                  render={({ field }) => {
                    return (
                      <TextField
                        {...field}
                        placeholder={t("placeholder.instagramProfile")}
                        fullWidth
                        variant="outlined"
                        error={!!errors.instagram_profile}
                        helperText={errors.instagram_profile?.message}
                      />
                    );
                  }}
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                  }}
                >
                  {t("businessPage.field.telephone")}
                </Typography>
                <Controller
                  name="telephone"
                  control={control}
                  render={({ field }) => {
                    return (
                      <MuiTelInput
                        disabled
                        value={field.value || ""}
                        onChange={field.onChange}
                        defaultCountry="BR"
                        placeholder={t("placeholder.companyPhone")}
                        forceCallingCode
                        fullWidth
                        error={!!errors.telephone}
                        helperText={
                          errors.telephone
                            ? t("businessPage.error.telephone")
                            : ""
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: alpha(theme.palette.grey[100], 0.5),
                          },
                        }}
                      />
                    );
                  }}
                />
              </Box>
            </Box>
          </Stack>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        maxWidth: 800,
        mx: "auto",
        display: "grid",
        gap: 3,
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: "center" }}>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 80,
            height: 80,
            mb: 2,
          }}
        >
          <img
            src={businessIcon}
            alt="Business"
            style={{
              width: "80px",
              height: "80px",
              objectFit: "contain",
            }}
          />
        </Box>
        <Typography
          variant="h5"
          sx={{ fontWeight: 600, color: theme.palette.primary.main, mb: 1 }}
        >
          {t("businessPage.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("Configure your company information")}
        </Typography>
      </Box>

      {/* Stepper */}
      <Card
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.tertiary.main, 0.02)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        <CardContent>
          <Stepper
            alternativeLabel
            activeStep={activeStep}
            connector={<CustomConnector />}
          >
            {steps.map(label => (
              <Step key={label}>
                <StepLabel StepIconComponent={CustomStepIcon}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {t(label)}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      <Divider />

      {/* Form Content */}
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        {renderStepContent()}

        {/* Info Card for disabled fields */}
        {(activeStep === 0 || activeStep === 1 || activeStep === 2) && (
          <Card
            sx={{
              mt: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.05)} 0%, ${alpha(theme.palette.info.main, 0.02)} 100%)`,
              border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
            }}
          >
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <InfoOutlined
                  sx={{ mr: 1, color: theme.palette.info.main, fontSize: 20 }}
                />
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: theme.palette.info.main }}
                >
                  {t("Information")}
                </Typography>
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: "0.875rem" }}
              >
                {activeStep === 0 &&
                  t(
                    "Company Name and Business ID fields cannot be changed after registration.",
                  )}
                {activeStep === 1 &&
                  t(
                    "Address information cannot be changed. Contact support if necessary.",
                  )}
                {activeStep === 2 &&
                  t(
                    "Email and main phone cannot be changed for security reasons.",
                  )}
              </Typography>
            </CardContent>
          </Card>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Action Buttons */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <Button
            type="button"
            variant="outlined"
            onClick={() => setActiveStep(s => Math.max(s - 1, 0))}
            disabled={activeStep === 0}
            sx={{
              fontWeight: 600,
              py: 1.5,
              borderRadius: 2,
              flex: { xs: 1, sm: "initial" },
              minWidth: 120,
            }}
          >
            {t("common.back")}
          </Button>

          <Button
            type="button"
            variant="contained"
            disabled={isSubmitting}
            onClick={() => {
              if (activeStep < steps.length - 1) {
                setActiveStep(s => s + 1);
              } else {
                handleSubmit(onSubmit)();
              }
            }}
            sx={{
              fontWeight: 600,
              py: 1.5,
              borderRadius: 2,
              flex: { xs: 1, sm: "initial" },
              minWidth: 120,
            }}
          >
            {activeStep < steps.length - 1
              ? t("common.next")
              : isSubmitting
                ? t("businessPage.saving")
                : t("businessPage.button.saveCompany")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
